import { Constants, DateUtils, PersonDoc, RfiCommonUtils, RfiDoc, RFISettingsDoc, TagData } from 'common';
import { ProjectDoc, Workflow, WorkflowState } from 'common';
import { ConcourseDb, TeamDoc, User } from '../../global';
import { removeDupes } from '../../global/utils/string';

/**
 * Pure utility methods for RFI document manipulation, defaults, and business logic.
 * No DB communication here — see RfiService for that.
 */
export class RfiUtils extends RfiCommonUtils {
  static DEFAULT_RFI_WORKFLOW: Workflow = {
    id: 'default',
    name: 'Default RFI Workflow',
    states: [
      {
        id: 'create',
        pastTense: 'Created',
        action: 'create',
        dueDays: 2,
        dueBy: [],
      },
      {
        id: 'answer',
        action: 'Answer',
        pastTense: 'Answered',
        dueDays: 5,
        dueBy: [],
      },
      {
        id: 'approve',
        action: 'Approve',
        pastTense: 'Approved',
        dueDays: 5,
        dueBy: [],
      },
    ],
  };

  static DEFAULT_RFI_SETTINGS: RFISettingsDoc = {
    fields: ['distribution', 'tasks', 'tags', 'attachments'],
    states: RfiUtils.DEFAULT_RFI_WORKFLOW.states,
  };

  static getBaseRfi(states: WorkflowState[], num: string): RfiDoc {
    return {
      _id: ConcourseDb.makeShortDocId(),
      code: num,
      subject: '',
      description: '',
      due_date: '',
      states,
      successors: [],
      accepted_answers: [],
      distribution: [],
      tags: [],
      status_history: [],
      question: '',
      suggested_answer: '',
    };
  }
/*
  static rfiFromSettings(project: ProjectDoc, user: User): RfiDoc {
    const workflows = project.submittals_workflows || [];
    workflows.unshift(RfiUtils.DEFAULT_RFI_WORKFLOW);
    const workflow = workflows[workflows.length - 1];
    const dueDays = workflow.states.reduce((sum, st) => sum + st.dueDays, 0);
    const dueDate = new Date(DateUtils.currentDate.getTime() + dueDays * DateUtils.MILLIS_PER_DAY);

    return {
      ...RfiUtils.getBaseRfi(workflow.states),
      due_date: dueDate.toISOString(),
    };
  }
    */

  static statesFromTemplate(settings: RFISettingsDoc): WorkflowState[] {
    return settings.states.map(state => ({ ...state, doneBy: null, doneDate: null }));
  }

  static addDistribution(doc: RfiDoc, person: PersonDoc, userId: string) {
    if (doc.distribution.includes(person._id)) return;
    doc.distribution = [...doc.distribution, person._id];
    return doc;
  }

  static addTeamDistribution(doc: RfiDoc, team: TeamDoc, userId: string): RfiDoc {
    doc.distribution = removeDupes([...doc.distribution, ...team.members]);
    return doc;
  }

  static removeDistribution(doc: RfiDoc, personId: string, userId: string): RfiDoc {
    doc.distribution = doc.distribution.filter(id => id !== personId);
    return doc;
  }

  static addTag(doc: RfiDoc, tag: TagData): RfiDoc {
    if (doc.tags.find(t => t.name == tag.name)) return;
    doc.tags = [...doc.tags, tag];
    return doc;
  }

  static removeTag(doc: RfiDoc, tag: TagData) {
    doc.tags = doc.tags.filter(t => t.name != tag.name);
    return doc;
  }

  static addTask(doc: RfiDoc, taskId: number, type: string, lag: number): RfiDoc {
    doc.successors = [...(doc.successors || []), { task_unique_id: taskId, type, lag }];
    return doc;
  }

  static removeTask(doc: RfiDoc, taskId: number) {
    doc.successors = doc.successors.filter(c => c.task_unique_id != taskId);
    return doc;
  }

  static toggleAcceptedAnswer(doc: RfiDoc, id: string) {
    const indexOf = doc.accepted_answers.indexOf(id);
    if (indexOf > -1) {
      doc.accepted_answers.splice(indexOf, 1);
    } else {
      doc.accepted_answers.push(id);
    }
  }

  static setAcceptedAnswer(doc: RfiDoc, id: string, user: User, text: string): RfiDoc {
    const indexOf = doc.accepted_answers?.indexOf(id);
    if (indexOf > -1) {
      doc.accepted_answers.splice(indexOf, 1);
    } else {
      doc.accepted_answers.push(id);
    }
    return doc;
  }

  static isAcceptedAnswer(doc: RfiDoc, id: string) {
    return doc.accepted_answers.includes(id);
  }

  static isDraft(doc: RfiDoc): boolean {
    return !doc.states || !doc.created_date || doc.created_date === '';
  }

  static contains(doc: RfiDoc, text: string): boolean {
    return doc.code == text.toLocaleLowerCase() || doc.subject.toLocaleLowerCase().includes(text.toLocaleLowerCase());
  }

  static hasPeople(doc: RfiDoc, people: PersonDoc[]): boolean {
    return people.filter(person => RfiUtils.hasPersonId(doc, person._id)).length > 0;
  }

  static hasPersonId(doc: RfiDoc, personId: string): boolean {
    return doc.assignees?.indexOf(personId) >= 0;
  }

  static renderRfiName(doc?: RfiDoc, chars?: number, fallback?: string): string {
    let fullName = '';
    if (!doc && !fallback) {
      return '';
    }

    if (!doc) {
      fullName = fallback;
    } else {
      fullName = `#${doc.code} - ${doc.subject}`;
    }

    if (chars && fullName.length > chars) {
      return `${fullName.substring(0, chars)}...`;
    }

    return fullName;
  }
}
